import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterReducerState, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { periodToString } from 'src/app/modules/audits/utils/report.utils';
import { ReportsService } from 'src/app/modules/report-components/services/report.service';
import { loadReportEvent } from 'src/app/modules/report/tracking/events';
import { formattedReportDate } from 'src/app/modules/report/tracking/utils';
import { TrackingService } from 'src/app/modules/shared/services/tracking.service';
import { ReportPageState } from '.';
import { isPreviewPageSelector, isReportsPageSelector } from '../header/header.selectors';
import * as fromActions from './report.actions';
import * as fromSelectors from './report.selectors';

@Injectable()
export class ReportPageEffects {
  constructor(
    private actions$: Actions,
    private reports: ReportsService,
    private store: Store<{
      report: ReportPageState;
      router: RouterReducerState;
    }>,
    private tracking: TrackingService,
    private snackBar: MatSnackBar,
  ) {}

  loadReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadReport),
      withLatestFrom(
        this.store.select(fromSelectors.selectReportId),
        this.store.select(fromSelectors.selectCachedReport),
        this.store.select(fromSelectors.selectCustomParams),
      ),
      mergeMap(([action, params, cachedReport, customParams]) => {
        const hasCustomParam =
          customParams.sectionId >= 0 || customParams.sop || customParams.employee !== undefined || customParams.category !== undefined;
        if (!hasCustomParam && cachedReport) {
          return of(
            fromActions.setCurrentReport({
              report: cachedReport,
              cache: false,
            }),
          );
        }

        const getter = !hasCustomParam
          ? this.reports.getReport(params.areaId, params.periodId, action.isPreview)
          : this.reports.getCustomReport(
              params.areaId,
              params.periodId,
              {
                sectionId: customParams.sectionId >= 0 ? customParams.sectionId : undefined,
                employee: customParams.employee,
                category: customParams.category,
                sopPre: customParams.sop ? customParams.sop.sopPre : undefined,
                sopPost: customParams.sop ? customParams.sop.sopPost : undefined,
              },
              action.isPreview,
            );

        return getter.pipe(
          tap(res => {
            if (res.error) {
              throw new Error(res.error);
            }
          }),
          map(res =>
            fromActions.setCurrentReport({
              report: res.data,
              cache: !hasCustomParam,
            }),
          ),
          catchError(error => of(fromActions.reportLoadError({ error: error.message }))),
        );
      }),
    ),
  );

  loadDates$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.loadReport),
        mergeMap(() => this.store.select(fromSelectors.selectCachedDates).pipe(first())),
        tap(({ dates, areaId }) => {
          if (!dates) {
            this.store.dispatch(fromActions.loadAreaDates({ areaId }));
            this.store.dispatch(fromActions.loadAreaMonths({ areaId }));
          }
        }),
      ),
    { dispatch: false },
  );

  loadAreaDates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadAreaDates),
      mergeMap(action =>
        this.reports.getAreaAuditDates(action.areaId).pipe(
          tap(res => {
            if (res.error) {
              throw new Error(res.error);
            }
          }),
          map(res => fromActions.setAreaDates({ dates: res.data, areaId: action.areaId })),
        ),
      ),
    ),
  );

  loadAreaMonths$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadAreaMonths),
      mergeMap(action =>
        this.reports.getAreaAuditMonths(action.areaId).pipe(
          tap(res => {
            if (res.error) {
              throw new Error(res.error);
            }
          }),
          map(res =>
            fromActions.setAreaMonths({
              months: res.data.reverse(),
              areaId: action.areaId,
            }),
          ),
          // catchError((error:Error) => of(fromActions.))
        ),
      ),
    ),
  );

  handleUrlChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      mergeMap(() => this.store.select(isReportsPageSelector).pipe(first())),
      filter(Boolean),
      mergeMap(() => this.store.select(fromSelectors.selectReportId).pipe(first())),
      // mergeMap(params => this.store.select(selectCachedAuditReport(+params.areaId, params.periodId)).pipe(first())),
      withLatestFrom(this.store.select(fromSelectors.selectCurrentReport)),
      map(([cachedReport, currentReport]) => {
        let hydrateWith = cachedReport;

        // If the report is already loaded, and it's the same report, then use it for hydration.
        // Happens when SOP / bar is changed for the current report - URL changes but the report id is the same.
        if (
          currentReport &&
          cachedReport &&
          currentReport.areaId === cachedReport.areaId &&
          currentReport.periodId === cachedReport.periodId
        ) {
          hydrateWith = currentReport;
        }

        return fromActions.loadReport({
          hydrateWith: null,
          isPreview: false,
        });
      }),
    ),
  );

  handlePreviewUrlChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      mergeMap(() => this.store.select(isPreviewPageSelector).pipe(first())),
      filter(Boolean),
      map(() => fromActions.loadReport({ hydrateWith: null, isPreview: true })),
    ),
  );

  trackReportLoading$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.setCurrentReport),
        tap(action => {
          if (!action.report) {
            return;
          }
          this.tracking.track(
            loadReportEvent({
              area: action.report.area,
              date: formattedReportDate(action.report.settings.start, action.report.period),
              location: action.report.location,
              period: periodToString(action.report.period),
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  loadPosCategorySop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPosCategorySop),
      mergeMap(action =>
        this.reports.loadPosCategorySop(action.customerId, action.locationId).pipe(
          map(res => fromActions.loadPosCategorySopSuccess({ sop: res })),
          catchError((err: Error) => of(fromActions.loadPosCategorySopFail({ error: err }))),
        ),
      ),
    ),
  );

  updatePosCategorySop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.updatePosCategorySop),
      mergeMap(action =>
        this.reports.updatePosCategorySop(action.customerId, action.locationId, action.sop).pipe(
          map(res => fromActions.updatePosCategorySopSuccess()),
          catchError((err: Error) => {
            this.snackBar.open('Failed to update POS Category SOP', 'Ok', { duration: 5000 });
            return of(fromActions.updatePosCategorySopFail({ error: err }));
          }),
        ),
      ),
    ),
  );

  showUpdateSuccessSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.updatePosCategorySopSuccess),
        tap(() => this.snackBar.open('POS Category SOP updated successfully', 'Ok', { duration: 3000 })),
      ),
    { dispatch: false },
  );

  triggerSalesLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setCurrentReport),
      filter(action => action.report.period === 1),
      map(action =>
        fromActions.loadAuditSales({
          areaId: action.report.areaId,
          periodId: action.report.periodId,
        }),
      ),
    ),
  );

  loadAuditSales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadAuditSales),
      mergeMap(action =>
        this.reports.getAuditSalesData(action.areaId, action.periodId).pipe(
          map(res => fromActions.loadAuditSalesSuccess({ sales: res })),
          catchError((err: Error) => of(fromActions.loadAuditSalesFail(err))),
        ),
      ),
    ),
  );
}
